'use strict';

module.exports = function ($uibModal, $q, Integration, Department, $translate) {
    const toId = (value) => {
        if (value == null || value === '') {
            return '';
        }
        if (typeof value === 'string' || typeof value === 'number') {
            return String(value);
        }
        if (typeof value === 'object') {
            if (value.$oid) {
                return String(value.$oid);
            }
            if (value.id != null && (typeof value.id === 'string' || typeof value.id === 'number')) {
                return String(value.id);
            }
            if (typeof value.toString === 'function') {
                const asString = value.toString();
                if (asString && asString !== '[object Object]') {
                    return asString;
                }
            }
        }
        return '';
    };

    const escapeHtml = (value) => {
        return String(value == null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    };

    const groupFields = (fields, departments) => {
        const deptNameById = {};
        (departments || []).forEach((dept) => {
            if (dept && dept.id != null) {
                deptNameById[toId(dept.id)] = dept.name;
            }
        });

        const groups = {};
        (fields || []).forEach((field) => {
            if (!field) {
                return;
            }
            const formId = toId(field.formId);
            if (!formId) {
                return;
            }
            const departmentId = toId(field.departmentId);
            const groupKey = departmentId || '__none__';
            if (!groups[groupKey]) {
                groups[groupKey] = {
                    departmentId: departmentId,
                    departmentName: departmentId
                        ? (deptNameById[departmentId] || departmentId)
                        : $translate.instant('DASHBOARD.DASH_INTEG.GLOBAL'),
                    formsById: {},
                };
            }
            if (!groups[groupKey].formsById[formId]) {
                groups[groupKey].formsById[formId] = {
                    id: formId,
                    name: field.formName || formId,
                    departmentId: departmentId,
                };
            }
        });

        return Object.keys(groups).map((key) => {
            const group = groups[key];
            const forms = Object.keys(group.formsById).map((id) => group.formsById[id]);
            forms.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            return {
                departmentId: group.departmentId,
                departmentName: group.departmentName,
                forms: forms,
            };
        }).sort((a, b) => (a.departmentName || '').localeCompare(b.departmentName || ''));
    };

    const fieldsFromResult = (result) => {
        const mapping = result || {};
        if (Array.isArray(mapping)) {
            return mapping.reduce((acc, item) => {
                if (item && Array.isArray(item.fields)) {
                    return acc.concat(item.fields);
                }
                return acc.concat(item || []);
            }, []);
        }
        if (Array.isArray(mapping.fields)) {
            return mapping.fields;
        }
        return [];
    };

    const resolveDepartmentNames = (groups) => {
        const missingIds = groups
            .filter((group) => group.departmentId && group.departmentName === group.departmentId)
            .map((group) => group.departmentId);
        if (!missingIds.length || typeof Department.find !== 'function') {
            return $q.when(groups);
        }
        return Department.find({
            filter: {
                where: { id: { inq: missingIds } },
                fields: { id: true, name: true },
            },
        }).$promise.then((found) => {
            const nameById = {};
            (found || []).forEach((dept) => {
                if (dept && dept.id != null && dept.name) {
                    nameById[toId(dept.id)] = dept.name;
                }
            });
            groups.forEach((group) => {
                if (nameById[group.departmentId]) {
                    group.departmentName = nameById[group.departmentId];
                }
            });
            groups.sort((a, b) => (a.departmentName || '').localeCompare(b.departmentName || ''));
            return groups;
        }).catch(() => groups);
    };

    const loadGroups = (integrationId, departments) => {
        if (!integrationId || typeof Integration.findForms !== 'function') {
            return $q.reject({ associatedForms: true });
        }

        return Integration.findForms({ integrationId: integrationId })
            .$promise
            .then((result) => {
                return resolveDepartmentNames(groupFields(fieldsFromResult(result), departments));
            })
            .catch((err) => {
                console.error(err);
                return $q.reject({ associatedForms: true });
            });
    };

    const open = (integrationId, departments) => {
        if (!integrationId) {
            return;
        }

        return $uibModal.open({
            animation: true,
            templateUrl: 'dashboard-integration/associatedFormsModal.html',
            controller: [
                '$uibModalInstance',
                'integrationId',
                'departments',
                function ($uibModalInstance, integrationId, departments) {
                    var _this = this;
                    _this.loading = true;
                    _this.groups = [];
                    _this.empty = false;
                    _this.error = false;

                    _this.close = () => {
                        $uibModalInstance.dismiss();
                    };

                    const finish = (groups, hasError) => {
                        _this.groups = groups || [];
                        _this.empty = !hasError && _this.groups.length === 0;
                        _this.error = !!hasError;
                        _this.loading = false;
                    };

                    loadGroups(integrationId, departments)
                        .then((groups) => {
                            finish(groups, false);
                        })
                        .catch(() => {
                            finish([], true);
                        });
                },
            ],
            controllerAs: 'ctrl',
            size: 'lg',
            windowClass: 'dialog',
            bindToController: true,
            resolve: {
                integrationId: function () {
                    return integrationId;
                },
                departments: function () {
                    return departments || [];
                },
            },
        });
    };

    const confirmInUse = (integration, departments) => {
        const integrationId = integration && integration.id;
        return loadGroups(integrationId, departments).then((groups) => {
            if (!groups || !groups.length) {
                return;
            }

            return $uibModal.open({
                animation: true,
                templateUrl: 'dashboard-integration/deleteIntegrationInUseModal.html',
                controller: [
                    '$uibModalInstance',
                    '$sce',
                    'groups',
                    'integrationName',
                    function ($uibModalInstance, $sce, groups, integrationName) {
                        var _this = this;
                        _this.groups = groups;
                        _this.message = $sce.trustAsHtml($translate.instant('DASHBOARD.DASH_INTEG.DIAG_DEL.IN_USE', {
                            name: escapeHtml(integrationName),
                        }));

                        _this.continue = () => {
                            $uibModalInstance.close();
                        };

                        _this.cancel = () => {
                            $uibModalInstance.dismiss();
                        };
                    },
                ],
                controllerAs: 'ctrl',
                size: 'md',
                windowClass: 'dialog',
                bindToController: true,
                resolve: {
                    groups: function () {
                        return groups;
                    },
                    integrationName: function () {
                        return integration && integration.name;
                    },
                },
            }).result;
        });
    };

    return {
        open: open,
        confirmInUse: confirmInUse,
    };
};
